import React from 'react';
import { GET_USELOG_BY_LICENSE } from '../reports.graphql';
import { useQuery } from '@apollo/client/react';
import { Box, Typography, Paper, IconButton, Stack } from '@mui/material';
import LoadingSpinner from '../../components/LoadingSpinner';
import XLSX from 'xlsx';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ExcelLogo from '../../assets/microsoft-excel.svg?react';
import { DataGridPro, GridColDef } from '@mui/x-data-grid-pro';
import { nbNO } from '@mui/x-data-grid/locales';

interface UseLogModel {
  numberOfVisit: number;
  userId: number;
  firstName: string;
  lastName: string;
  email: string;
  medium: string;
  displayName: string;
}

const formatReportDate = (date) => {
  if (!date) {
    return 'I dag';
  }
  if (typeof date.format === 'function') {
    return date.format('DD.MM.YYYY');
  }
  return new Date(date).toLocaleDateString('nb-NO');
};

const renderBreadCrumb = (data, callback, selectedGuide, customerName, showInternalBackButton, fromDate, toDate) => {
  const dateRange = `${formatReportDate(fromDate)} - ${formatReportDate(toDate)}`;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'space-between',
        gap: 2,
        marginBottom: '16px',
        flexDirection: { xs: 'column', md: 'row' },
      }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        {showInternalBackButton && (
          <IconButton onClick={callback} title="Gå tilbake til kundeliste">
            <ArrowBackIcon />
          </IconButton>
        )}
        <Box>
          <Typography variant="h5">{customerName}</Typography>
          <Typography variant="body2" color="text.secondary">
            Brukere og søk for {selectedGuide}
          </Typography>
        </Box>
      </Box>
      <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', alignSelf: { xs: 'flex-end', md: 'center' } }}>
        <Typography variant="caption" color="text.secondary">
          Periode: {dateRange}
        </Typography>
        {data && <UseLogTableExcelExport model={data.uselogs} />}
      </Stack>
    </Box>
  );
};
const renderTable = (data, loading) => {
  const rows = (data?.uselogs ?? []).map((x: UseLogModel) => ({
    id: x.userId,
    displayName: x.displayName,
    numberOfVisit: x.numberOfVisit,
    medium: x.medium,
  }));

  const columns: GridColDef<(typeof rows)[number]>[] = [
    { field: 'displayName', headerName: 'Bruker navn', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
    { field: 'numberOfVisit', headerName: 'Antall søk', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
    { field: 'medium', headerName: 'Logget inn med', minWidth: 160, flex: 1, align: 'left', headerAlign: 'left' },
  ];

  return (
    <Paper elevation={1} sx={{ p: 2 }}>
      <DataGridPro
        autoHeight
        density="compact"
        rows={rows}
        columns={columns}
        localeText={nbNO.components.MuiDataGrid.defaultProps.localeText}
        pagination
        pageSizeOptions={[25, 50, 100]}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 50,
              page: 0,
            },
          },
        }}
        sx={{
          '& .MuiDataGrid-columnHeaderTitle': {
            fontWeight: 700,
          },
        }}
      />
      {loading && <LoadingSpinner />}
    </Paper>
  );
};
export default function UseLogTable(props) {
  const { guideId, customerId, fromDate, toDate, callback, customerName, selectedGuide, email, showInternalBackButton = true } = props;
  const { loading, data } = useQuery<{ uselogs: UseLogModel[] }>(GET_USELOG_BY_LICENSE, {
    variables: { licenseId: customerId, guideId: guideId, fromDate: fromDate, toDate: toDate, email: email },
  });
  return (
    <>
      {renderBreadCrumb(data, callback, selectedGuide, customerName, showInternalBackButton, fromDate, toDate)}
      {renderTable(data, loading)}
    </>
  );
}
function UseLogTableExcelExport({ model }: { model: UseLogModel[] }) {
  const exportFile = () => {
    const workbook = XLSX.utils.book_new();
    const heading: string[] = ['Bruker navn', 'Antall søk', 'Logget inn med'];
    const workBookName = 'Antall søk per bruker';
    const data = model.map((x) => [x.displayName, x.numberOfVisit, x.medium]);
    const input = [heading, ...data];
    const sheet = XLSX.utils.aoa_to_sheet(input);
    XLSX.utils.book_append_sheet(workbook, sheet, workBookName);
    XLSX.writeFile(workbook, 'brukantall.xlsx');
  };
  return (
    <IconButton onClick={exportFile} title="Eksport til Excel" size="large">
      <ExcelLogo />
    </IconButton>
  );
}
